import type { MiniAppManifest, MiniAppShellRailConfig } from '../../../../../../mini-apps/_shared/types';
import { resolveShellConfig, toolsSlugFromPathname } from '../../../../../../mini-apps/_shared/shell-config';
import type { RailOverrides } from './rail/rail-items';

export type HeaderVariant = 'flow-tabs' | 'search';

/**
 * Pure resolver for what chrome the app-wide frame shows on a route.
 * `frame: 'none'` means the route opts out of the V2 frame entirely (Delphi):
 * `ShellLayout` wraps it in the legacy rail, configured by `legacyRail`.
 * `actionsHidden` is the manifest's `shell.hideEnrollQueue` on both branches.
 */
export type ShellChrome =
  | { frame: 'none'; legacyRail: MiniAppShellRailConfig | undefined; actionsHidden: boolean }
  | { frame: 'v2'; header: HeaderVariant; rail: RailOverrides; actionsHidden: boolean };

// Delphi keeps its own minimal rail, search and chrome AS IS (Spencer 10/13).
// Identified by slug here, not a manifest key, so `mini-apps/delphi/manifest.json`
// and `_shared/types.ts` stay unchanged.
const NO_FRAME_SLUGS: ReadonlySet<string> = new Set(['delphi']);

// Lowercased like `toolsSlugFromPathname`: the router matches `/Flow/x` too.
const isFlowPath = (pathname: string) => {
  const lower = pathname.toLowerCase();
  return lower === '/flow' || lower.startsWith('/flow/');
};

export function resolveChrome(pathname: string, manifests: MiniAppManifest[]): ShellChrome {
  const slug = toolsSlugFromPathname(pathname);
  const shell = resolveShellConfig(pathname, manifests);
  const actionsHidden = shell?.hideEnrollQueue ?? false;
  if (slug !== null && NO_FRAME_SLUGS.has(slug)) {
    return { frame: 'none', legacyRail: shell?.rail, actionsHidden };
  }

  const header = isFlowPath(pathname) ? 'flow-tabs' : 'search';
  return { frame: 'v2', header, rail: shell?.rail ?? {}, actionsHidden };
}
